// True-Colors-style personality survey. The user ranks 4 phrases per row from
// "most like me" (4 pts) to "least like me" (1 pt). Scores per color summed
// across rows produce a primary and secondary temperament.

import type { Language } from './prefs.js';

export type ColorKey = 'blue' | 'gold' | 'green' | 'orange';

export interface ColorMeta {
  key: ColorKey;
  label: string;       // user-facing label
  tagline: string;     // 1-line essence
  hex: string;         // swatch color
}

export const COLOR_META: Record<ColorKey, ColorMeta> = {
  blue:   { key: 'blue',   label: 'Blue',   tagline: 'Relational, harmonizing, authentic',  hex: '#5b8def' },
  gold:   { key: 'gold',   label: 'Gold',   tagline: 'Structured, dutiful, dependable',     hex: '#d4a02a' },
  green:  { key: 'green',  label: 'Green',  tagline: 'Analytical, curious, independent',    hex: '#43a47a' },
  orange: { key: 'orange', label: 'Orange', tagline: 'Spontaneous, energetic, action-driven', hex: '#e89146' },
};

export interface RowItem { color: ColorKey; text: string }
export interface SurveyRow { theme: string; items: RowItem[] }

type Bank = Record<Language, SurveyRow[]>;

export const SURVEY: Bank = {
  fr: [
    { theme: 'En général',
      items: [
        { color: 'blue',   text: 'Authentique, harmonieux, à l’écoute des autres' },
        { color: 'gold',   text: 'Organisé, responsable, fiable' },
        { color: 'green',  text: 'Curieux, analytique, indépendant' },
        { color: 'orange', text: 'Spontané, énergique, charmant' },
      ] },
    { theme: 'Au travail / dans tes projets',
      items: [
        { color: 'blue',   text: 'J’aide, je soutiens, je communique' },
        { color: 'gold',   text: 'Je planifie, je structure, je livre' },
        { color: 'green',  text: 'Je résous des problèmes complexes' },
        { color: 'orange', text: 'Je passe à l’action, j’improvise' },
      ] },
    { theme: 'Avec les proches',
      items: [
        { color: 'blue',   text: 'Empathique, idéaliste, médiateur' },
        { color: 'gold',   text: 'Loyal, traditionnel, engagé' },
        { color: 'green',  text: 'Rationnel, autonome, direct' },
        { color: 'orange', text: 'Ludique, libre, plein d’humour' },
      ] },
    { theme: 'Ce qui te motive',
      items: [
        { color: 'blue',   text: 'Le sens, les relations, la croissance personnelle' },
        { color: 'gold',   text: 'La stabilité, l’ordre, le devoir bien fait' },
        { color: 'green',  text: 'La connaissance, la maîtrise, la compétence' },
        { color: 'orange', text: 'La liberté, la variété, l’action' },
      ] },
    { theme: 'Sous pression',
      items: [
        { color: 'blue',   text: 'Je cherche la paix, j’évite le conflit' },
        { color: 'gold',   text: 'Je m’accroche aux règles et au plan' },
        { color: 'green',  text: 'Je me retire pour réfléchir' },
        { color: 'orange', text: 'Je deviens impulsif, j’agis' },
      ] },
  ],
  en: [
    { theme: 'In general',
      items: [
        { color: 'blue',   text: 'Authentic, harmonious, attuned to others' },
        { color: 'gold',   text: 'Organized, responsible, reliable' },
        { color: 'green',  text: 'Curious, analytical, independent' },
        { color: 'orange', text: 'Spontaneous, energetic, charming' },
      ] },
    { theme: 'At work / in projects',
      items: [
        { color: 'blue',   text: 'I help, I support, I communicate' },
        { color: 'gold',   text: 'I plan, I structure, I deliver' },
        { color: 'green',  text: 'I solve complex problems' },
        { color: 'orange', text: 'I act, I improvise' },
      ] },
    { theme: 'With loved ones',
      items: [
        { color: 'blue',   text: 'Empathetic, idealistic, peacemaker' },
        { color: 'gold',   text: 'Loyal, traditional, committed' },
        { color: 'green',  text: 'Rational, autonomous, direct' },
        { color: 'orange', text: 'Playful, free, full of humor' },
      ] },
    { theme: 'What motivates you',
      items: [
        { color: 'blue',   text: 'Meaning, relationships, personal growth' },
        { color: 'gold',   text: 'Stability, order, doing my duty well' },
        { color: 'green',  text: 'Knowledge, mastery, competence' },
        { color: 'orange', text: 'Freedom, variety, action' },
      ] },
    { theme: 'Under pressure',
      items: [
        { color: 'blue',   text: 'I seek peace, I avoid conflict' },
        { color: 'gold',   text: 'I cling to rules and the plan' },
        { color: 'green',  text: 'I withdraw to think it through' },
        { color: 'orange', text: 'I become impulsive, I act' },
      ] },
  ],
  es: [
    { theme: 'En general',
      items: [
        { color: 'blue',   text: 'Auténtico, armonioso, atento a los demás' },
        { color: 'gold',   text: 'Organizado, responsable, confiable' },
        { color: 'green',  text: 'Curioso, analítico, independiente' },
        { color: 'orange', text: 'Espontáneo, enérgico, encantador' },
      ] },
    { theme: 'En el trabajo / proyectos',
      items: [
        { color: 'blue',   text: 'Ayudo, apoyo, comunico' },
        { color: 'gold',   text: 'Planifico, estructuro, entrego' },
        { color: 'green',  text: 'Resuelvo problemas complejos' },
        { color: 'orange', text: 'Actúo, improviso' },
      ] },
    { theme: 'Con seres queridos',
      items: [
        { color: 'blue',   text: 'Empático, idealista, mediador' },
        { color: 'gold',   text: 'Leal, tradicional, comprometido' },
        { color: 'green',  text: 'Racional, autónomo, directo' },
        { color: 'orange', text: 'Juguetón, libre, con humor' },
      ] },
    { theme: 'Lo que te motiva',
      items: [
        { color: 'blue',   text: 'Sentido, relaciones, crecimiento personal' },
        { color: 'gold',   text: 'Estabilidad, orden, el deber bien hecho' },
        { color: 'green',  text: 'Conocimiento, maestría, competencia' },
        { color: 'orange', text: 'Libertad, variedad, acción' },
      ] },
    { theme: 'Bajo presión',
      items: [
        { color: 'blue',   text: 'Busco la paz, evito el conflicto' },
        { color: 'gold',   text: 'Me aferro a las reglas y al plan' },
        { color: 'green',  text: 'Me retiro a pensar' },
        { color: 'orange', text: 'Me vuelvo impulsivo, actúo' },
      ] },
  ],
};

export interface PersonalitySurveyResult {
  scores: Record<ColorKey, number>;       // 5–20 per color (5 rows × 1..4)
  percents: Record<ColorKey, number>;     // 0–100
  primary: ColorKey;
  secondary: ColorKey;
  takenAt: string;
}

/**
 * rankings: per row index, an array of ColorKey ordered from most-like-me (4 pts)
 * to least-like-me (1 pt). Must have all four colors per row.
 */
export function scoreSurvey(rankings: ColorKey[][]): PersonalitySurveyResult {
  const scores: Record<ColorKey, number> = { blue: 0, gold: 0, green: 0, orange: 0 };
  for (const row of rankings) {
    row.forEach((color, i) => { scores[color] += 4 - i; });
  }
  const total = Object.values(scores).reduce((s, v) => s + v, 0);
  const percents: Record<ColorKey, number> = { blue: 0, gold: 0, green: 0, orange: 0 };
  for (const k of Object.keys(scores) as ColorKey[]) {
    percents[k] = total > 0 ? Math.round((scores[k] / total) * 100) : 0;
  }
  const sorted = (Object.keys(scores) as ColorKey[]).sort((a, b) => scores[b] - scores[a]);
  return {
    scores,
    percents,
    primary: sorted[0]!,
    secondary: sorted[1]!,
    takenAt: new Date().toISOString(),
  };
}
